import axios from 'axios';

// Vera chat API. AI is optional: a 503 means the tenant has no AI provider.
const vera = axios.create({
  baseURL: '/api/v1.0/vera',
  timeout: 65000,
  headers: { 'Content-Type': 'application/json' },
});

vera.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const veraService = {
  // history: [{ role:'user'|'assistant', content }]
  chat: (message, history = [], context) => vera.post('/chat', { message, history, context }),
  // Execute a Tier-2/3 action the user approved (returns {status, message, resultRef}).
  executeAction: ({ action, payload }) => vera.post('/actions/execute', { action, payload }),

  // ── Server-side conversations (history survives browsers/devices) ──────────
  listConversations: () => vera.get('/conversations').then((r) => r.data?.conversations || []),
  createConversation: (title) => vera.post('/conversations', { title }).then((r) => r.data),
  getConversation: (guid) => vera.get(`/conversations/${encodeURIComponent(guid)}`).then((r) => r.data),
  renameConversation: (guid, title) => vera.put(`/conversations/${encodeURIComponent(guid)}`, { title }),
  deleteConversation: (guid) => vera.delete(`/conversations/${encodeURIComponent(guid)}`),
  appendMessage: (guid, message) =>
    vera.post(`/conversations/${encodeURIComponent(guid)}/messages`, message).then((r) => r.data),
  sendFeedback: (messageId, value) => vera.post(`/messages/${messageId}/feedback`, { value }),
};

const b64ToText = (b64) => {
  try {
    const bin = atob(b64);
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch { return ''; }
};
const safeJson = (s) => { try { return JSON.parse(s); } catch { return null; } };

/**
 * Streaming chat over SSE (fetch — lets us send the JWT, unlike EventSource).
 * Calls onToken(text) as the answer streams, onArtifacts(arr), onMeta({model}),
 * onError(e), onDone(). Returns an AbortController-friendly promise.
 */
export async function streamChat({ message, history = [], context }, handlers = {}) {
  const { onToken, onArtifacts, onArtifactPending, onMeta, onSignals, onError, onDone, signal } = handlers;
  const token = localStorage.getItem('authToken');
  let res;
  try {
    res = await fetch('/api/v1.0/vera/chat/stream', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ message, history, context }),
      signal,
    });
  } catch (e) { onError?.(e); onDone?.(); return; }
  if (!res.ok || !res.body) { onError?.({ status: res.status }); onDone?.(); return; }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let sep;
      while ((sep = buf.indexOf('\n\n')) >= 0) {
        const raw = buf.slice(0, sep); buf = buf.slice(sep + 2);
        let event = 'message';
        const dataLines = [];
        raw.split('\n').forEach((line) => {
          if (line.startsWith('event:')) event = line.slice(6).trim();
          else if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
        });
        const data = dataLines.join('\n');
        if (event === 'token') onToken?.(b64ToText(data));
        else if (event === 'artifacts') onArtifacts?.(safeJson(data) || []);
        else if (event === 'artifact_pending') onArtifactPending?.((safeJson(data) || {}).count || 0);
        else if (event === 'meta') onMeta?.(safeJson(data) || {});
        else if (event === 'signals') onSignals?.(safeJson(data) || {});
        else if (event === 'error') onError?.(safeJson(data) || { error: data });
        else if (event === 'done') { /* handled by stream end */ }
      }
    }
  } catch (e) { onError?.(e); }
  onDone?.();
}

export default veraService;
